import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import type { InviteEligibilityApplicant } from '@/modules/application-tracker/api';

import { InviteEligibilityBanner } from './invite-eligibility-banner';
import {
  useInviteEligibilityCheck,
  type InviteEligibilityRowFilter,
} from '../hooks/use-invite-eligibility-check';
import { useInviteEligibilityTracking } from '../hooks/use-invite-eligibility-tracking';

/**
 * One event per banner occurrence. A recheck of the same renter that comes back with the same
 * result is the same banner, so the brief hide while the check reruns does not count as a new one.
 */
const useTrackBannerShown = ({
  isChecked,
  bannerApplicant,
  email,
  index,
}: {
  isChecked: boolean;
  bannerApplicant: InviteEligibilityApplicant | undefined;
  email: string;
  index: number;
}) => {
  const { trackBannerShown } = useInviteEligibilityTracking();
  const trackedBannerKeyRef = useRef<string | null>(null);
  const bannerKey = bannerApplicant
    ? `${bannerApplicant.outcome}|${bannerApplicant.reason}|${email}`
    : null;

  useEffect(() => {
    if (!isChecked) {
      return;
    }
    if (bannerApplicant && bannerKey !== trackedBannerKeyRef.current) {
      trackBannerShown(bannerApplicant, index);
    }
    trackedBannerKeyRef.current = bannerKey;
  }, [isChecked, bannerKey, bannerApplicant, index, trackBannerShown]);
};

const resolveBanner = (
  applicant: InviteEligibilityApplicant | undefined,
  formEmail: string | null | undefined,
) => {
  const email = applicant?.email.trim() || formEmail?.trim() || '';
  return {
    email,
    bannerApplicant: applicant && applicant.outcome !== 'allow' && email ? applicant : undefined,
  };
};

export function InviteEligibilityApplicantNotice({
  index,
  includeRow,
  inviteAlphaId,
}: {
  index: number;
  /** Must match the controller's filter so the banner reads the same query. */
  includeRow?: InviteEligibilityRowFilter;
  /** Must match the controller's value so the banner reads the same query. */
  inviteAlphaId?: string;
}) {
  const { isFeatureEnabled, isStale, getApplicant } = useInviteEligibilityCheck({
    includeRow,
    inviteAlphaId,
  });
  const formEmail = useWatch({ name: `applicants.${index}.email` }) as string | null | undefined;
  const { trackRecordLinkClicked } = useInviteEligibilityTracking();

  const isChecked = isFeatureEnabled && !isStale;
  const { bannerApplicant, email } = resolveBanner(
    isChecked ? getApplicant(index) : undefined,
    formEmail,
  );
  useTrackBannerShown({ isChecked, bannerApplicant, email, index });

  if (!bannerApplicant || bannerApplicant.outcome === 'allow') {
    return null;
  }

  return (
    <InviteEligibilityBanner
      outcome={bannerApplicant.outcome}
      email={email}
      totalMatches={bannerApplicant.total_matches}
      reason={bannerApplicant.reason}
      onCtaClick={() => trackRecordLinkClicked(bannerApplicant)}
    />
  );
}
